import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

export type Density = "compact" | "normal" | "cozy" | "comfort";
export type Zoom = 75 | 90 | 100 | 125 | 150;

interface Props {
  density: Density;
  zoom: Zoom;
  groupsExpanded: boolean;
  onDensityChange: (d: Density) => void;
  onZoomChange: (z: Zoom) => void;
  onGroupsExpandedChange: (expanded: boolean) => void;
}

const DENSITY_LABEL: Record<Density, string> = {
  compact: "Compact",
  normal: "Normal",
  cozy: "Cozy",
  comfort: "Comfort",
};

export default function CompactViewMenu({ density, zoom, groupsExpanded, onDensityChange, onZoomChange, onGroupsExpandedChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs text-ink2 px-2.5 py-1.5 rounded-md hover:bg-[#EEF4FB]"
      >
        {DENSITY_LABEL[density]} | {zoom}% <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-[#D8DEE6] rounded-lg shadow-lg z-50 py-1.5 min-w-[200px]">
          <Section>
            <Item checked={density === "compact"} onClick={() => onDensityChange("compact")}>Compact View</Item>
            <Item checked={density === "normal"} onClick={() => onDensityChange("normal")}>Normal View</Item>
            <Item checked={density === "cozy"} onClick={() => onDensityChange("cozy")}>Cozy View</Item>
            <Item checked={density === "comfort"} onClick={() => onDensityChange("comfort")}>Comfort View</Item>
          </Section>
          <div className="h-px bg-[#EEF1F4] my-1.5" />
          <Section>
            <Item checked={groupsExpanded} onClick={() => onGroupsExpandedChange(true)}>Expand All</Item>
            <Item checked={!groupsExpanded} onClick={() => onGroupsExpandedChange(false)}>Collapse All</Item>
          </Section>
          <div className="h-px bg-[#EEF1F4] my-1.5" />
          <Section>
            {([75, 90, 100, 125, 150] as Zoom[]).map((z) => (
              <Item key={z} checked={zoom === z} onClick={() => onZoomChange(z)}>Zoom To {z}%</Item>
            ))}
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>;
}

function Item({ checked, onClick, children }: { checked: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 pl-9 text-[12.5px] text-ink hover:bg-[#EEF4FB] relative"
    >
      {checked && <Check className="w-3.5 h-3.5 text-teal-dark absolute left-3 top-1/2 -translate-y-1/2" />}
      {children}
    </button>
  );
}